import type { ReactNode } from 'react';
import { COMPANY, LEGAL_LAST_UPDATED, isPlaceholder } from './company';
import { PublicLayout } from './PublicLayout';

export interface LegalSection {
  /** Anchor, so a reviewer can be sent straight to e.g. /privacy#connected-email. */
  id: string;
  heading: string;
  body: ReactNode;
}

/**
 * Shared shape of the privacy policy and the terms: a title, a contents list
 * of anchors, then numbered sections. Prose styling lives here so the two
 * documents cannot drift apart in look.
 */
export function LegalPage({
  title,
  intro,
  sections,
}: {
  title: string;
  intro: ReactNode;
  sections: LegalSection[];
}) {
  return (
    <PublicLayout>
      <div className="bg-slate-950 text-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-300">{COMPANY.product}</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
          <p className="mt-3 text-sm text-slate-400">Last updated {LEGAL_LAST_UPDATED}</p>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[220px_1fr]">
        <nav aria-label="Contents" className="lg:sticky lg:top-24 lg:self-start">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Contents</p>
          <ol className="space-y-1.5 text-sm">
            {sections.map((s, i) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-slate-600 hover:text-[#0089c3]">
                  {i + 1}. {s.heading}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="max-w-3xl text-[15px] leading-relaxed text-slate-700">
          <div className="mb-10 space-y-4">{intro}</div>
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className="mb-10 scroll-mt-24">
              <h2 className="mb-3 text-xl font-semibold text-slate-900">
                {i + 1}. {s.heading}
              </h2>
              <div className="space-y-4">{s.body}</div>
            </section>
          ))}
        </article>
      </div>
    </PublicLayout>
  );
}

/** Bulleted list in the legal prose. */
export function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc space-y-1.5 pl-5 marker:text-slate-400">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

/** A labelled term inside a bullet: "<b>Label</b> — text". */
export function Term({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <strong className="font-semibold text-slate-900">{label}</strong> — {children}
    </>
  );
}

/** An email address that is only a link once it has stopped being a placeholder. */
export function Email({ address }: { address: string }) {
  if (isPlaceholder(address)) return <span>{address}</span>;
  return (
    <a href={`mailto:${address}`} className="font-medium text-[#0089c3] hover:underline">
      {address}
    </a>
  );
}
